import styled from "styled-components";
import { MouseEvent, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { AnimatePresence } from "framer-motion";
import { useForm, useWatch } from "react-hook-form";
import { useQuery, useQueryClient } from "@tanstack/react-query";

import Button from "@/components/Button";
import { PageHeading, TeamHeading } from "@/components/Layout/Parts";
import Text from "@/components/Text";
import ToggableHeading from "@/components/ToggableHeading";
import { api } from "@/utils/api";
import { EventsProjectResource } from "@/types/resources";
import useAlerts from "@/hooks/useAlerts";
import ContentSpinner from "@/components/ContentSpinner";
import EventsModal from "../ProductionEvents/components/EventsModal";
import { categories, facebookRooms } from "@/utils/data/productionEvents";
import ProjectEventBlock from "./components/ProjectEventBlock";
import { handleFormErrors } from "@/utils/helpers";
import { EventsStatusEnum } from "@/utils/enums";
import ProductionEventButtons from "./components/ProductionEventButtons";
import BackButton from "@/components/BackButton";

export type FormDataT = {
  should_create_events: boolean;
  events: {
    id: number;
    starts_at: string;
    deleted: boolean;
    availabilityChecked: boolean;
    name: string;
    duration: number;
    calendar_event_name: string;
    location_type?: string | null;
    meeting_room?: string | null;
  }[];
};

const HeadingRow = styled.div`
  display: flex;
  justify-content: space-between;
`;

const MainInfoBox = styled.div`
  background: ${({ theme }) => theme.colors.white};
  box-shadow: 0px 0px 2px rgba(0, 0, 0, 0.08);
  border-radius: 4px;
  padding: 24px;
  position: relative;
  margin-bottom: 56px;
`;

const CategoryHeading = styled(Text)`
  margin-bottom: 16px;
`;

const Label = styled(Text)`
  margin-bottom: 4px;
`;

const MainInfoRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  border-bottom: 1px solid ${({ theme }) => theme.colors.lightGray};
  margin-bottom: 24px;
  > div {
    width: 50%;
    padding-bottom: 24px;
    &:nth-child(odd) {
      border-right: 1px solid ${({ theme }) => theme.colors.lightGray};
      padding-right: 24px;
    }
    &:nth-child(even) {
      padding-left: 24px;
    }
    &:nth-last-child(1),
    &:nth-last-child(2) {
      padding-bottom: 0;
      margin-bottom: 24px;
    }
  }
`;

const CategoryRow = styled.div`
  &:last-child {
    > div {
      &:last-child {
        margin-bottom: 0;
        border-bottom: 0;
        > div {
          &:nth-last-child(1),
          &:nth-last-child(2) {
            padding-bottom: 0;
            margin-bottom: 0;
          }
        }
      }
    }
  }
`;

const EditButton = styled(Button)`
  position: absolute;
  top: 24px;
  right: 24px;
  font-size: 14px;
  height: 38px;
  min-width: 90px;
  padding: 0 10px;
`;

const Buttons = styled.div`
  display: flex;
  gap: 16px;
  justify-content: flex-end;
`;

const FooterButtons = styled(Buttons)`
  margin-top: 32px;
`;

const ProductionEvent = () => {
  const [shouldCreateEvents, setShouldCreateEvents] = useState(false);

  const [showEventModal, setShowEventModal] = useState(false);

  const { teamId, id } = useParams();

  const [isOpen, setIsOpen] = useState(true);

  const queryClient = useQueryClient();

  const [areEventsOpen, setAreEventsOpen] = useState(true);

  const navigate = useNavigate();

  const alerts = useAlerts();

  const { isLoading, data: project } = useQuery<EventsProjectResource>(
    ["production_events_projects", { id }],
    () =>
      api.get<never, EventsProjectResource>(
        `/v1/production/events-projects/${id}`
      ),
    {
      staleTime: 60 * 1000,
      retry: 2,
      onError: (err) => {
        if ((err as any).response.status === 404) {
          return navigate("/404", {
            replace: true,
          });
        }
        alerts.error();
      },
    }
  );

  const {
    handleSubmit,
    setError,
    control,
    getValues,
    clearErrors,
    reset,
    setValue,
    formState: { isSubmitting },
  } = useForm<FormDataT>({});

  useEffect(() => {
    if (project?.events.length) {
      reset({
        events: project.events.map((event) => ({
          id: event.id,
          starts_at: event.starts_at,
          deleted: !!event.deletedAt,
          availabilityChecked: false,
          name: event.name,
          meeting_room: null,
          location_type: event.meetingRoom
            ? facebookRooms.includes(event.meetingRoom)
              ? "facebook"
              : "youtube"
            : null,
          calendar_event_name: event.calendarEventName ?? event.name,
          duration: event.duration
            ? event.duration
            : event.name === "Pre - Production Meeting (PPM)"
            ? 120
            : 60,
        })),
      });
    }
    // eslint-disable-next-line
  }, [reset, project?.id]);

  const eventStartsAt = useWatch({
    name: "events",
    control,
  });

  const onSubmit = (
    e: MouseEvent<HTMLButtonElement>,
    shouldCreateEvents: boolean
  ) => {
    clearErrors();
    return handleSubmit(async (data) => {
      try {
        const notCheckedEvents = data.events.filter(
          (event) => !event.availabilityChecked && !event.deleted
        );
        if (shouldCreateEvents && notCheckedEvents.length) {
          const confirm = await alerts.confirm(
            "Availability of some event attendees wasn't checked",
            `${notCheckedEvents.map((event) => " " + event.name)}`
          );

          if (!confirm) return;
        }

        await api.post<FormDataT, never>(
          `/v1/production/events-projects/${project?.id}/schedule`,
          {
            events: data.events
              .filter((event) => !event.deleted)
              .map((event) => {
                return {
                  id: event.id,
                  starts_at: event.starts_at,
                  duration: event.duration,
                  calendar_event_name: event.calendar_event_name,
                  meeting_room: shouldCreateEvents ? event.meeting_room : null,
                };
              }),
            should_create_events: shouldCreateEvents,
          }
        );

        queryClient.invalidateQueries(["production_events_projects"], {
          exact: false,
        });

        alerts.success(
          `${
            shouldCreateEvents
              ? "Events created succesfully"
              : "Project was saved to drafts"
          }`
        );
      } catch (err) {
        handleFormErrors<FormDataT>(err, setError, alerts, true);
      }
    })(e);
  };

  const onEventCancel = async () => {
    try {
      const confirm = await alerts.confirm(
        `Are you sure you want to cancel these events?`
      );

      if (!confirm) return;

      await api.post<never, { id: number }>(
        `/v1/production/events-projects/${project?.id}/cancel`
      );

      queryClient.invalidateQueries(["production_events_projects"], {
        exact: false,
      });

      alerts.success("Events were successfully canceled");
    } catch (e) {
      alerts.error();
    }
  };

  const onDelete = async () => {
    try {
      const confirm = await alerts.confirm(
        `Are you sure you want to delete?`,
        project!.name
      );

      if (!confirm) return;

      await api.delete<never, { id: number }>(
        `/v1/production/events-projects/${project!.id}`
      );

      queryClient.invalidateQueries(["production_events_projects"], {
        exact: false,
      });

      alerts.success("Event project was successfully deleted");

      navigate(`/teams/${teamId}/production/events/sent`, {
        replace: true,
      });
    } catch (e) {
      alerts.error();
    }
  };

  if (isLoading || !project) {
    return <ContentSpinner />;
  }

  return (
    <>
      <TeamHeading />
      <HeadingRow>
        <PageHeading>Setup Overview</PageHeading>
        <Buttons>
          <ProductionEventButtons
            status={project.status}
            onEventCancel={onEventCancel}
            setShouldCreateEvents={setShouldCreateEvents}
            onSubmit={onSubmit}
            isSubmitting={isSubmitting}
            shouldCreateEvents={shouldCreateEvents}
            authorEmail={project.authorEmail}
            eventStartsAt={eventStartsAt}
            events={project.events}
            onDelete={onDelete}
          />
        </Buttons>
      </HeadingRow>
      <BackButton link={`/teams/${teamId}/production/events/sent`} />
      <ToggableHeading
        heading="PROJECT CREW"
        onToggle={() => setIsOpen((prev) => !prev)}
        isOpen={isOpen}
      />
      {isOpen && (
        <MainInfoBox>
          <EditButton
            disabled={project.status === EventsStatusEnum.SENT}
            color="secondary"
            onClick={() => setShowEventModal(true)}
          >
            Edit
          </EditButton>
          <CategoryHeading $type="button">Main Info</CategoryHeading>
          <MainInfoRow>
            <div>
              <Label $type="smallHeadline" $color="gray">
                Project Name
              </Label>
              <Text $type="caption">{project?.name}</Text>
            </div>
            <div>
              <Label $type="smallHeadline" $color="gray">
                Compilation ID
              </Label>
              <Text $type="caption">{project?.compilationId ?? "-"}</Text>
            </div>
            <div>
              <Label $type="smallHeadline" $color="gray">
                Channel Owner Email
              </Label>
              <Text $type="caption">{project?.channelOwner?.email ?? "-"}</Text>
            </div>
          </MainInfoRow>
          {categories
            .filter((c) => c.category !== "Art Lead")
            .map((categoryGroup, index) => (
              <CategoryRow key={index}>
                <CategoryHeading $type="button">
                  {categoryGroup.category}
                </CategoryHeading>
                <MainInfoRow>
                  {categoryGroup.category === "Art" && (
                    <>
                      {project?.participants
                        .filter(
                          (participant) => participant.category === "Art Lead"
                        )
                        .map((participant) => (
                          <div key={participant.id}>
                            <Label $type="smallHeadline" $color="gray">
                              Art Lead Email
                            </Label>
                            <Text $type="caption">{participant.email}</Text>
                          </div>
                        ))}
                    </>
                  )}
                  {project?.participants
                    .filter(
                      (participant) =>
                        participant.category === categoryGroup.category
                    )
                    .map((participant) => (
                      <div key={participant.id}>
                        <Label $type="smallHeadline" $color="gray">
                          {categoryGroup.label} Email
                        </Label>
                        <Text $type="caption">{participant.email}</Text>
                      </div>
                    ))}
                </MainInfoRow>
              </CategoryRow>
            ))}
        </MainInfoBox>
      )}
      <ToggableHeading
        heading="SCHEDULE EVENTS"
        onToggle={() => setAreEventsOpen((prev) => !prev)}
        isOpen={areEventsOpen}
      />
      {areEventsOpen && (
        <>
          {project.events.map((event, index) => (
            <ProjectEventBlock
              control={control}
              index={index}
              event={event}
              status={project.status}
              key={index}
              getValues={getValues}
              setValue={setValue}
            />
          ))}
        </>
      )}
      <FooterButtons>
        <ProductionEventButtons
          status={project.status}
          onEventCancel={onEventCancel}
          setShouldCreateEvents={setShouldCreateEvents}
          onSubmit={onSubmit}
          isSubmitting={isSubmitting}
          shouldCreateEvents={shouldCreateEvents}
          authorEmail={project.authorEmail}
          eventStartsAt={eventStartsAt}
          events={project.events}
          onDelete={onDelete}
        />
      </FooterButtons>
      <AnimatePresence>
        {showEventModal && (
          <EventsModal
            project={project}
            onClose={() => setShowEventModal(false)}
          />
        )}
      </AnimatePresence>
    </>
  );
};

export default ProductionEvent;
